// Picker de "imágenes ya generadas" para adjuntar al chat del Asistente.
// Lista las imágenes activas de `imagenes_generadas` de la empresa (bucket
// `generadas`), de más nuevas a más viejas, y permite elegir VARIAS. Al
// aceptar, las devuelve como adjuntos de referencia.
//
// Mismo patrón visual que ProductFullMenu: mobile = bottom sheet (portal),
// desktop = side panel a la derecha.

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import CachedImg from '../../components/CachedImg';
import ProductGridSkeleton from '../../components/ProductGridSkeleton';

const GENERADAS_BUCKET = 'generadas';
const SIGNED_TTL = 3600;

export interface GeneratedPick {
  storagePath: string;
  previewUrl: string;
  bucket: string;
}

interface GeneratedRow {
  id: number;
  storagePath: string;
  signedUrl: string;
}

const GeneratedImagesPicker: React.FC<{
  open: boolean;
  companyId: string | null;
  onCancel: () => void;
  onAccept: (picks: GeneratedPick[]) => void;
}> = ({ open, companyId, onCancel, onAccept }) => {
  const [animated, setAnimated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<GeneratedRow[]>([]);
  const [selected, setSelected] = useState<number[]>([]);

  useEffect(() => {
    if (open) requestAnimationFrame(() => setAnimated(true));
    else {
      setAnimated(false);
      setSelected([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from('imagenes_generadas')
        .select('id, file_name, created_at')
        .eq('id_company', companyId)
        .eq('active', true)
        .not('file_name', 'is', null)
        .order('created_at', { ascending: false })
        .limit(60);
      if (error || !data) {
        if (!cancelled) {
          setRows([]);
          setLoading(false);
        }
        return;
      }
      const signed = await Promise.all(
        data.map(async (r: any) => {
          const storagePath = `${companyId}/full/${r.file_name}`;
          const { data: s } = await supabase.storage
            .from(GENERADAS_BUCKET)
            .createSignedUrl(storagePath, SIGNED_TTL);
          if (!s?.signedUrl) return null;
          return {
            id: r.id as number,
            storagePath,
            signedUrl: s.signedUrl,
          } as GeneratedRow;
        }),
      );
      if (!cancelled) {
        setRows(signed.filter((x): x is GeneratedRow => !!x));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  if (!open) return null;

  const close = (cb: () => void) => {
    setAnimated(false);
    setTimeout(cb, 280);
  };

  const toggle = (id: number) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const accept = () => {
    const byId = new Map(rows.map((r) => [r.id, r]));
    const picks: GeneratedPick[] = selected
      .map((id) => byId.get(id))
      .filter((r): r is GeneratedRow => !!r)
      .map((r) => ({
        storagePath: r.storagePath,
        previewUrl: r.signedUrl,
        bucket: GENERADAS_BUCKET,
      }));
    close(() => onAccept(picks));
  };

  const grid = (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {rows.map((r) => {
        const isSel = selected.includes(r.id);
        return (
          <button
            key={r.id}
            type="button"
            onClick={() => toggle(r.id)}
            className={`relative aspect-square rounded-[13px] overflow-hidden bg-neutral-100 transition-all active:scale-[0.98] ${
              isSel
                ? 'ring-2 ring-brand-primary'
                : 'ring-1 ring-black/[0.06]'
            }`}
          >
            <CachedImg
              src={r.signedUrl}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover"
            />
            {isSel && (
              <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white shadow-sm">
                <Check className="w-3.5 h-3.5" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  const body = loading ? (
    <ProductGridSkeleton
      count={6}
      gridClassName="grid grid-cols-2 sm:grid-cols-3 gap-3"
      tileClassName="rounded-[13px]"
    />
  ) : rows.length === 0 ? (
    <div className="text-center text-[14px] text-[#3C3C43]/55 py-16 px-6">
      Todavía no generaste ninguna imagen.
    </div>
  ) : (
    grid
  );

  const acceptLabel = selected.length > 0 ? `Aceptar (${selected.length})` : 'Aceptar';

  return ReactDOM.createPortal(
    <>
      {/* ── Mobile bottom sheet ───────────────────────────────────────── */}
      <div
        className={`md:hidden fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => close(onCancel)}
      />
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 z-[211] bg-white rounded-t-[13px] flex flex-col transition-transform duration-300 ease-out ${
          animated ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          height: 'calc(100dvh - 1rem - env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        <div className="py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        <div className="flex items-center justify-between px-4 pb-3 shrink-0">
          <button
            type="button"
            onClick={() => close(onCancel)}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
          >
            Cancelar
          </button>
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            Imágenes generadas
          </h3>
          <button
            type="button"
            onClick={accept}
            disabled={selected.length === 0}
            className="text-[15px] font-semibold text-brand-primary active:opacity-50 transition-opacity w-20 text-right disabled:opacity-30"
          >
            Aceptar
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pt-3">{body}</div>
      </div>

      {/* ── Desktop side panel ────────────────────────────────────────── */}
      <div
        className={`hidden md:block fixed inset-0 z-[210] bg-black/20 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => close(onCancel)}
      />
      <div
        className={`hidden md:flex fixed top-4 right-4 bottom-4 w-[560px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[211] flex-col overflow-hidden transition-transform duration-300 ease-out ${
          animated ? 'translate-x-0' : 'translate-x-[calc(100%+1rem)]'
        }`}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-4 shrink-0">
          <h3 className="text-[18px] font-semibold text-brand-black/90">
            Imágenes generadas
          </h3>
          <button
            type="button"
            onClick={() => close(onCancel)}
            aria-label="Cerrar"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-brand-black/70 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-4">{body}</div>
        <div className="px-6 py-4 border-t border-black/[0.06] shrink-0">
          <button
            type="button"
            onClick={accept}
            disabled={selected.length === 0}
            className="w-full py-3 rounded-xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-semibold shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {acceptLabel}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default GeneratedImagesPicker;
